import { useMutation, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { ApiError, CONVERSATION_CONTENT_ROLE } from 'utils/types';
import { toastError } from 'ui/partials/Toast';
import { useAdminParams } from 'utils/helpers';
import { withAcl } from 'hooks/Api';

import { CONTENTS, CONVERSATIONS } from '../utils/constants';

interface Payload {
  id: string;
  content: string;
  meta?: Record<string, any>;
  files?: File[];
}

export default function useSendMessage() {
  const queryClient = useQueryClient();
  const { accountId, accountUserId } = useAdminParams();
  return useMutation<any, ApiError, Payload, any>({
    mutationFn: async (payload) => {
      const { id, content } = payload;
      const { url } = await withAcl({
        account: {
          // eslint-disable-next-line max-len
          url: `${import.meta.env.VITE_APP_END_POINT}/api/v1/channels/toad/conversations/${id}/contents/stream`,
        },
        admin: {
          // eslint-disable-next-line max-len
          url: `${import.meta.env.VITE_APP_END_POINT}/api/v2/accounts/${accountId}/users/${accountUserId}/channels/toad/conversations/${id}/contents/stream`,
        },
      });

      queryClient.setQueryData([CONVERSATIONS, id, CONTENTS], (prev: any) => {
        const _pages = [...prev];

        _pages.push({
          _id: new Date().toISOString(),
          content: payload.content,
          role: CONVERSATION_CONTENT_ROLE.user,
          toadConversationId: payload.id,
        });

        return _pages;
      });

      const token = Cookies.get('_token');

      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({ content, role: 'user' }),
        headers: [
          ['Accepts', 'application/json, text/plain, */*'],
          ['Content-Type', 'application/json'],
          ['Access-Control-Allow-Origin', '*'],
          ['Access-Control-Allow-Credentials', 'true'],
          ['Authorization', `Bearer ${token}`],
        ],
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return response.body;
    },
    onSuccess: async (data, payload) => {
      console.log('success', data, payload);
      queryClient.setQueryData(
        [CONVERSATIONS, payload.id, CONTENTS],
        (prev: any) => {
          const _pages = [...prev];

          _pages.push({
            _id: payload.content,
            content: '',
            role: 'assistant',
            toadConversationId: payload.id,
          });

          return _pages;
        },
      );

      const reader = data?.getReader();
      const decoder = new TextDecoder();
      let botMessage = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (reader) {
          const { done, value } = await reader.read();
          if (done) break;
          const decodedMessage = decoder.decode(value, { stream: true });
          botMessage += decodedMessage;

          queryClient.setQueryData(
            [CONVERSATIONS, payload.id, CONTENTS],
            (prev: any) => {
              const _pages = [...prev];

              const messageIndex = _pages.findIndex(
                (p: any) => p._id === payload.content,
              );

              _pages[messageIndex] = {
                _id: payload.content,
                content: botMessage,
                role: 'assistant',
                toadConversationId: payload.id,
              };

              return _pages;
            },
          );

          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth',
          });
        }
      }
    },
    onError: (error: any) => {
      toastError(error.message);
    },
  });
}
